import { Injectable } from '@angular/core';
import { toast } from 'ngx-sonner';
import { ToastComponent } from '@enginuity/core/organisms/toast/toast.component';
import { ToastData } from '@services/ui-services/models/toast-data';
import { BehaviorSubject } from 'rxjs';

export const ACCEPTED_TOAST_DATA: ToastData = {
  name: 'Accepted',
  message: 'accepted',
  info: 'successfully',
  time: '',
  toastIcons: {
    success: 'check',
    cancel: 'cancel',
    avatar: 'type',
  },
  type: 'success',
  State: 'default',
  primaryButton: false,
  secondaryButton: false,
  showControls: false,
};

export interface ActiveToast {
  id: string | number;
  componentInstance?: any;
}

export type ToastType = 'success' | 'error' | 'info' | 'warning' | 'loading' | 'default';
export type ToastPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

export interface ToastOptions {
  position?: ToastPosition;
  duration?: number;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  cancel?: {
    label: string;
    onClick: () => void;
  };
  onDismiss?: (toast: any) => void;
  onAutoClose?: (toast: any) => void;
  useCustomComponent?: boolean;
  showCloseButton?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  // Map to store toast IDs for individual dismissal
  private toastRegistry = new Map<string, string | number>();

  // Track active toast count
  private activeToastCount = new BehaviorSubject<number>(0);
  public activeToastCount$ = this.activeToastCount.asObservable();

  static forRoot(): import('@angular/core').Provider {
    throw new Error('Method not implemented.');
  }

  // Method for components to dismiss themselves
  dismissToastByComponentId(componentId: string) {
    const toastId = this.toastRegistry.get(componentId);
    if (toastId) {
      toast.dismiss(toastId);
      this.toastRegistry.delete(componentId);
      this.updateToastCount();
    }
  }

  private updateToastCount() {
    this.activeToastCount.next(this.toastRegistry.size);
  }

  // Primary method - uses custom component by default for consistent theming
  showToast(message: string, type?: ToastType, options?: ToastOptions): ActiveToast;
  showToast(toastData: ToastData): ActiveToast;
  showToast(
    messageOrData: string | ToastData,
    type: ToastType = 'default',
    options?: ToastOptions
  ): ActiveToast {
    // Handle backward compatibility with old ToastData objects
    if (typeof messageOrData === 'object' && messageOrData !== null) {
      const toastData = messageOrData as ToastData;
      return this.showCustomToast(toastData, {
        position: 'bottom-left',
        duration: 3000,
        ...options,
      });
    }

    // Handle new string-based API - use custom component by default for consistent theming
    const message = messageOrData as string;
    const toastType = type || 'default';

    // Use custom component by default unless explicitly disabled
    if (options?.useCustomComponent !== false) {
      const toastData = {
        name: '',
        message: message,
        info: '',
        time: '',
        toastIcons: {
          success: 'check',
          cancel: 'cancel',
          avatar: 'type',
        },
        type: toastType === 'error' ? 'cancel' : toastType,
        State: 'default',
        primaryButton: options?.action
          ? {
              label: options.action.label,
              action: options.action.onClick,
            }
          : false,
        secondaryButton: false,
        showControls: true,
        showCloseButton: options?.showCloseButton !== false, // Show close button by default
      };

      return this.showCustomToast(toastData, options);
    }

    // Use built-in ngx-sonner toasts when explicitly requested
    return this.showBuiltInToast(message, toastType, options);
  }

  // Custom toast with your component (primary method)
  showCustomToast(toastData: any, options?: ToastOptions): ActiveToast {
    // Generate unique component ID
    const componentId = `toast_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Prepare componentProps without toastId first
    let toastId: string | number = '';
    const componentProps: any = {
      ...toastData,
      showCloseButton: options?.showCloseButton !== false,
      componentId: componentId,
      toastService: this,
    };
    // Create the toast, then assign toastId
    toastId = toast.custom(ToastComponent, {
      componentProps: { ...componentProps, toastId },
      duration: options?.duration || 3000,
      position: options?.position || 'bottom-left',
      onDismiss: dismissedToast => {
        // Clean up the registry when toast is dismissed
        this.toastRegistry.delete(componentId);
        this.updateToastCount();
        if (options?.onDismiss) {
          options.onDismiss(dismissedToast);
        }
      },
      onAutoClose: options?.onAutoClose,
    });

    // Register the toast ID with the component ID
    this.toastRegistry.set(componentId, toastId);
    this.updateToastCount();

    return {
      id: toastId,
      componentInstance: { ...toastData, componentId, toastId },
    };
  }

  // Built-in ngx-sonner toasts (alternative method for rich colors)
  showBuiltInToast(
    message: string,
    type: ToastType = 'default',
    options?: ToastOptions
  ): ActiveToast {
    let toastId: string | number;

    const toastOptions = {
      duration: options?.duration || 3000,
      position: options?.position || 'bottom-left',
      description: options?.description,
      action: options?.action,
      cancel: options?.cancel,
      onDismiss: (dismissedToast: any) => {
        // For built-in toasts, we can't track them in our registry
        // but we still need to call the user's onDismiss if provided
        if (options?.onDismiss) {
          options.onDismiss(dismissedToast);
        }
      },
      onAutoClose: options?.onAutoClose,
    };

    switch (type) {
      case 'success':
        toastId = toast.success(message, toastOptions);
        break;
      case 'error':
        toastId = toast.error(message, toastOptions);
        break;
      case 'info':
        toastId = toast.info(message, toastOptions);
        break;
      case 'warning':
        toastId = toast.warning(message, toastOptions);
        break;
      case 'loading':
        toastId = toast.loading(message, toastOptions);
        break;
      default:
        toastId = toast(message, toastOptions);
        break;
    }

    return {
      id: toastId,
      componentInstance: { message, type, ...options },
    };
  }

  // Convenience methods using custom component
  success(message: string, options?: ToastOptions): ActiveToast {
    return this.showToast(message, 'success', options);
  }

  error(message: string, options?: ToastOptions): ActiveToast {
    return this.showToast(message, 'error', options);
  }

  info(message: string, options?: ToastOptions): ActiveToast {
    return this.showToast(message, 'info', options);
  }

  warning(message: string, options?: ToastOptions): ActiveToast {
    return this.showToast(message, 'warning', options);
  }

  loading(message: string, options?: ToastOptions): ActiveToast {
    return this.showToast(message, 'loading', options);
  }

  // Promise toast for async operations
  promise<T>(
    promise: Promise<T> | (() => Promise<T>),
    options: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: any) => string);
      position?: ToastPosition;
      duration?: number;
      useCustomComponent?: boolean;
    }
  ): ActiveToast {
    // Use built-in promise handling as it's complex to implement with custom components
    const toastId = toast.promise(promise, {
      loading: options.loading,
      success: options.success,
      error: options.error,
      position: options.position || 'bottom-left',
      duration: options.duration,
    });

    return {
      id: toastId || '',
      componentInstance: options,
    };
  }

  dismissToast(activeToast: ActiveToast) {
    toast.dismiss(activeToast.id);
  }

  dismissAll() {
    toast.dismiss();
    // Clear the registry
    this.toastRegistry.clear();
    this.updateToastCount();
  }
}
